import React, { useState, useEffect } from "react";

// Ensure newline separates React/external from internal components
import ListingDisplayCard from "../components/P2P/ListingDisplayCard";
import { ProductCard } from "../components/ui-new/ResultGrid";
import { useAuthorisation } from "../hooks/useAuthorisation";

function BrowsePage() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [itemTypeFilter, setItemTypeFilter] = useState("all");
  const { getAccessTokenOrSignIn } = useAuthorisation();

  useEffect(() => {
    // Fetch all items via vector search
    const fetchItems = async () => {
      setIsLoading(true);
      setError(null);
      try {
        console.log("Fetching all items from vector search...");

        // Get authentication token
        const accessToken = await getAccessTokenOrSignIn();
        if (!accessToken) {
          throw new Error("Authentication required");
        }

        // Get API base URL from environment
        const apiBaseUrl =
          process.env.REACT_APP_API_BASE_URL || "";

        // Perform a broad vector search to get all items
        const response = await fetch(`${apiBaseUrl}/api/secure/query`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            query: "products clothing accessories items",
            inferredPreferences: {},
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const matches = data?.data?.matches || [];

        console.log(`Fetched ${matches.length} items from vector search`);

        // Map vector search results to unified format
        const mappedItems = matches.map((match) => ({
          ...match.metadata,
          id: match.id,
          item_type: match.metadata.item_type || "new",
          score: match.score,
        }));

        setItems(mappedItems);
      } catch (err) {
        console.error("Error fetching items:", err);
        setError(
          err.message || "Failed to load items. Please try again later."
        );
      }
      setIsLoading(false);
    };
    fetchItems();
  }, [getAccessTokenOrSignIn]);

  // Filter items based on selected tab
  const filteredItems = items.filter((item) => {
    if (itemTypeFilter === "all") return true;
    if (itemTypeFilter === "new") return item.item_type !== "p2p";
    if (itemTypeFilter === "p2p") return item.item_type === "p2p";
    return true;
  });

  return (
    <div className="pt-20 px-4 pb-4 md:pt-24 md:px-8 md:pb-8">
      <h1 className="text-2xl font-light mb-6 text-penta-black">
        Browse All Items
      </h1>

      {/* Filter tabs */}
      {items.length > 0 && (
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setItemTypeFilter("all")}
            className={`px-4 py-2 text-sm font-mono uppercase tracking-wide transition-colors duration-200 ${
              itemTypeFilter === "all"
                ? "bg-penta-black text-white"
                : "bg-penta-grey-light text-penta-black hover:bg-neutral-200"
            }`}
          >
            All ({items.length})
          </button>
          <button
            onClick={() => setItemTypeFilter("new")}
            className={`px-4 py-2 text-sm font-mono uppercase tracking-wide transition-colors duration-200 ${
              itemTypeFilter === "new"
                ? "bg-penta-black text-white"
                : "bg-penta-grey-light text-penta-black hover:bg-neutral-200"
            }`}
          >
            New ({items.filter((item) => item.item_type !== "p2p").length})
          </button>
          <button
            onClick={() => setItemTypeFilter("p2p")}
            className={`px-4 py-2 text-sm font-mono uppercase tracking-wide transition-colors duration-200 ${
              itemTypeFilter === "p2p"
                ? "bg-penta-black text-white"
                : "bg-penta-grey-light text-penta-black hover:bg-neutral-200"
            }`}
          >
            Pre-Owned ({items.filter((item) => item.item_type === "p2p").length}
            )
          </button>
        </div>
      )}

      {isLoading && <p className="text-penta-grey-medium">Loading items...</p>}
      {error && <p className="text-penta-coral">Error: {error}</p>}

      {!isLoading && !error && items.length === 0 && <p>No items found.</p>}

      {!isLoading && !error && filteredItems.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredItems.map((item) =>
            item.item_type === "p2p" ? (
              <ListingDisplayCard
                key={item.id}
                listing={{
                  id: item.id,
                  title: item.title,
                  price: item.price,
                  condition: item.condition,
                  image: item.thumbnail || item.image,
                  item_type: "p2p",
                }}
              />
            ) : (
              <ProductCard
                key={item.id}
                product={{
                  ...item,
                  image: item.thumbnail || item.image,
                  minPrice: item.price,
                  maxPrice: item.price,
                }}
                productCardClasses="flex flex-col cursor-pointer p-3 overflow-hidden bg-white border border-neutral-200 hover:border-penta-mint transition-colors duration-200"
                setSelectedProduct={() => {
                  // For now, just open the product link in a new tab
                  if (item.link) {
                    window.open(item.link, "_blank");
                  }
                }}
              />
            )
          )}
        </div>
      )}

      {!isLoading &&
        !error &&
        items.length > 0 &&
        filteredItems.length === 0 && <p>No {itemTypeFilter} items found.</p>}
    </div>
  );
}

export default BrowsePage;
